import mixpanel from "mixpanel-browser";
import { captureException } from "@sentry/vue";
import type User from "../datamodels/user";
import { MIXPANEL_TOKEN } from "@/shared/utils/env";

export const EVENT_NAMES = {
  Exercises: {
    ExerciseCreated: "ExerciseCreated",
    ExerciseDeleted: "ExerciseDeleted",
  },
  Feed: {
    FeedPostCreated: "FeedPostCreated",
    FeedPostPublished: "FeedPostPublished",
    FeedPostDraftSaved: "FeedPostDraftSaved",
    FeedPostDeleted: "FeedPostDeleted",
    FeedPostEdited: "FeedPostEdited",
    FeedFiltered: "FeedFiltered",
  },
  MEMBERSHIPS: {
    MembershipCanceled: "MembershipCanceled",
    MembershipParticipantChanged: "MembershipParticipantChanged",
  },
  Navigation: {
    NavAdminChat: "NavAdminChat",
    NavAdminEmail: "NavAdminEmail",
    NavAdminFeed: "NavAdminFeed",
    NavAdminTraining: "NavAdminTraining",
    NavAdminSubmissions: "NavAdminSubmissions",
    NavAdminWorkouts: "NavAdminWorkouts",
    NavAdminExercises: "NavAdminExercises",
    NavAdminUsers: "NavAdminUsers",
    NavAdminTeams: "NavAdminTeams",
    NavAdminTeam: "NavAdminTeam",
    NavAdminUser: "NavAdminUser",
    NavAdminWorkout: "NavAdminWorkout",
    NavAdminExercise: "NavAdminExercise",
    NavAdminVideoSubmission: "NavAdminVideoSubmission",
    NavAdminTrainingCollection: "NavAdminTrainingCollection",
    NavAdminStats: "NavAdminStats",
    NavAdminRegistrations: "NavAdminRegistrations",
    NavAdminMemberships: "NavAdminMemberships",
    NavAdminBookings: "NavAdminBookings",
    NavAdminSchedule: "NavAdminSchedule",
    NavAdminBookedSessions: "NavAdminBookedSessions",
    NavAdminNetworkManagement: "NavAdminNetworkManagement",
    NavAdminQuickLinks: "NavAdminQuickLinks",
    NavAdminOpenPortal: "NavAdminOpenPortal",
    NavAdminSupport: "NavAdminSupport",
  },
  OFFERINGS: {
    OfferingCreated: "OfferingCreated",
    OfferingEdited: "OfferingEdited",
    OpenEnrollment: "OfferingEnrollmentOpened",
    CloseEnrollment: "OfferingEnrollmentClosed",
    MakePublic: "OfferingMadePublic",
    MakePrivate: "OfferingMadePrivate",
    ParticipantEnrolled: "ParticipantEnrolled",
  },
  ORGANIZATION_SETTINGS: {
    UpdateOrganizationSettings: "UpdateOrganizationSettings",
    LinkAdded: "LinkAdded",
    LinkUpdated: "LinkUpdated",
    LinkDeleted: "LinkDeleted",
  },
  PASSWORD: {
    PasswordResetRequested: "PasswordResetRequested",
    PasswordResetFailed: "PasswordResetFailed",
    PasswordResetSucceeded: "PasswordResetSucceeded",
  },
  PAYMENTS: {
    InvoiceCreated: "InvoiceCreated",
    ScheduledPaymentAmountChanged: "ScheduledPaymentAmountChanged",
    ScheduledPaymentWaived: "ScheduledPaymentWaived",
  },
  REGISTRANTS: {
    RegistrantCanceled: "RegistrantCanceled",
    RegistrantRefunded: "RegistrantRefunded",
    RegistrantParticipantChanged: "RegistrantParticipantChanged",
  },
  STATS: {
    StatCategoryCreated: "StatCategoryCreated",
    StatCategoryUpdated: "StatCategoryUpdated",
  },
  TEAMS: {
    TEAM_CREATED: "TeamCreated",
    TEAM_DELETED: "TeamDeleted",
  },
  TrainingCollections: {
    TrainingCollectionCreated: "TrainingCollectionCreated",
    TrainingDrillCreated: "TrainingDrillCreated",
    TrainingComparisonVideoAdded: "TrainingComparisonVideoAdded",
    TrainingDrillExerciseAdded: "TrainingDrillExerciseAdded",
    TrainingDrillDeleted: "TrainingDrillDeleted",
  },
  USER: {
    UserCreated: "UserCreated",
    UserAddedToOrganization: "UserAddedToOrganization",
    UserMadeCoach: "UserMadeCoach",
    UserMadeAdmin: "UserMadeAdmin",
    AdminNavUser: "AdminNavUser",
    AdminLoggedIn: "AdminLoggedIn",
    UserEmailsCopied: "UserEmailsCopied",
  },
  VideoSubmissions: {
    SubmissionCoachChanged: "SubmissionCoachChanged",
    SubmissionVideoViewed: "SubmissionVideoViewed",
    SubmissionsRefreshed: "SubmissionsRefreshed",
    VideoSubmittedFromAdmin: "VideoSubmittedFromAdmin",
  },
  Workouts: {
    CalendarSelected: "CalendarSelected",
    CalendarCreated: "CalendarCreated",
    WorkoutCreated: "WorkoutCreated",
    WorkoutEdited: "WorkoutEdited",
    WorkoutDeleted: "WorkoutDeleted",
    WorkoutPasted: "WorkoutPasted",
    SetAdded: "SetAdded",
    SupersetAdded: "SupersetAdded",
    ExerciseBlockAdded: "ExerciseBlockAdded",
    CalendarAssigned: "CalendarAssigned",
  },
} as const;

export const addMixpanelEvent = (
  eventName: string,
  tags: Record<string, unknown> = {}
) => {
  try {
    mixpanel.track(eventName, tags);
  } catch (e: any) {
    captureException(e);
  }
};

export const mixpanelLogout = () => {
  if (MIXPANEL_TOKEN) {
    mixpanel.reset();
  }
};

export const mixpanelLogin = (user: User) => {
  if (MIXPANEL_TOKEN) {
    mixpanel.identify(user?.id.toString());

    // ios sets team, and teamId but not including as an Admin might be part of multiple teams
    mixpanel.people.set({
      $email: user?.email,
      role: user?.is_coach ? "Coach" : "Athlete",
      loginType: "Email",
      name: user?.name,
      userEmail: user?.email,
      userId: user?.id,
      orgId: user?.default_organization?.id,
      orgName: user?.default_organization?.name,
    });
  }
};
